import { ChangeEvent, KeyboardEvent, MouseEvent, useState } from 'react'
import Button from '@mui/material/Button'
import Popover from '@mui/material/Popover'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import { useTheme } from '@mui/material/styles'
import { PackItem } from '../packing'
import { useQuantityEntry } from '../useQuantityEntry'
import QuantityKeypad from './QuantityKeypad'

export type AddToCartonControlProps = {
	item: PackItem,
	/** The carton being filled, e.g. `Carton 2`. */
	cartonName: string,
	onAdd: ( units: number ) => void,
}

/**
 * Moving a product off the bench and into the carton being filled.
 *
 * One button. It adds everything left, which is what a box usually takes; a
 * number in the field beside it turns it into "Add 5" for a partial carton.
 * At a desk the field is typed into and Enter adds; on a tablet it opens the
 * on-screen pad, whose Add key adds.
 */
export default function AddToCartonControl( { item, cartonName, onAdd }: AddToCartonControlProps ) {
	const theme = useTheme()
	const entry = useQuantityEntry()
	const [ typed, setTyped ] = useState( '' )
	const [ keypadAnchor, setKeypadAnchor ] = useState<HTMLElement | null>( null )

	const spent = item.remaining === 0
	const clamp = ( digits: string ) => ( digits === '' ? '' : String( Math.min( Number( digits ), item.remaining ) ) )
	const units = typed === '' ? item.remaining : Number( typed )
	const name = item.identity.description

	const add = () => {
		if ( units > 0 ) {
			onAdd( units )
		}
		setTyped( '' )
	}

	const handleChange = ( event: ChangeEvent<HTMLInputElement> ) => setTyped( clamp( event.target.value.replace( /[^0-9]/g, '' ) ) )

	const handleKeyDown = ( event: KeyboardEvent<HTMLInputElement> ) => {
		if ( event.key === 'Enter' ) {
			event.preventDefault()
			add()
		}
	}

	const onKeypad = entry === 'keypad'
	const openKeypad = ( event: MouseEvent<HTMLElement> ) => {
		if ( !spent ) {
			setTyped( '' )
			setKeypadAnchor( event.currentTarget )
		}
	}

	return (
		<Stack direction="row" spacing={`${theme.touch.gap}px`} sx={{ flexShrink: 0 }}>
			<TextField
				value={typed}
				placeholder="Qty"
				disabled={spent}
				onChange={onKeypad ? undefined : handleChange}
				onKeyDown={onKeypad ? undefined : handleKeyDown}
				onClick={onKeypad ? openKeypad : undefined}
				slotProps={{
					htmlInput: {
						'aria-label': `Quantity of ${name} to add to ${cartonName}`,
						'inputMode': 'numeric',
						'readOnly': onKeypad,
						'sx': { textAlign: 'center', fontVariantNumeric: 'tabular-nums' },
					},
					input: { sx: { minHeight: theme.touch.control } },
				}}
				sx={{ width: 88 }}
			/>
			<Button
				variant="contained"
				disabled={spent}
				onClick={add}
				aria-label={typed === '' ? `Add all ${item.remaining} ${name} to ${cartonName}` : `Add ${typed} ${name} to ${cartonName}`}
				sx={{ minWidth: 104, minHeight: theme.touch.control, whiteSpace: 'nowrap' }}
			>
				{typed === '' ? 'Add all' : `Add ${typed}`}
			</Button>

			<Popover
				open={onKeypad && !!keypadAnchor}
				anchorEl={keypadAnchor}
				onClose={() => setKeypadAnchor( null )}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
				transformOrigin={{ vertical: 'top', horizontal: 'right' }}
			>
				<QuantityKeypad
					label={name}
					typed={typed}
					onTypedChange={( next ) => setTyped( clamp( next ) )}
					onDone={() => {
						setKeypadAnchor( null )
						add()
					}}
					doneLabel="Add"
				/>
			</Popover>
		</Stack>
	)
}
